import React from 'react';
import PaperclipIcon from './ui/PaperclipIcon';
import { buildAttachmentTooltip } from '../utils/orderFileTooltip';

/**
 * THE "this order has attachments" marker on the order LIST — one definition,
 * both layouts.
 *
 * Product owner, 2026-08-11: a buyer should be able to see from the list that an
 * order carries attachments, without expanding every row to find out. It sits
 * beside the **Ordered By** name in the table, fed by `order.file_count`
 * (the card layout's `Requested By:` site was deleted 2026-10-18).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IT COUNTS — AND THE COST OF THAT, RECORDED RATHER THAN DISCOVERED LATER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `file_count` is a bare `COUNT(*)` over `order_files` for the order, attached
 * by `GET /api/orders/department` and `GET /api/approvals/pending`. It counts
 * ORDER-LEVEL files, LINE-ITEM files and DELIVERY PAPERWORK alike.
 *
 * 🔑 Item files are the reason this exists at all: a quotation attached to a
 * line item was invisible in the expanded panel until 2026-08-11, and a marker
 * that skipped them would reproduce the same blindness one level up.
 *
 * ⚠️ **DELIVERY PAPERWORK COUNTS, AND THE COST IS REAL — ruled 2026-08-11, and
 * written here so that changing it later is an edit rather than a
 * rediscovery.** A list cell cannot legibly carry a category distinction, so one
 * clip means *"there are files here"*. But every delivered order accumulates
 * paperwork, so **the marker weakens over time as a "there is a quotation here"
 * signal** — in a department with a long delivery history, most rows will
 * eventually carry a clip. If the owner ever wants it to mean attachments only,
 * it is one `AND category != 'delivery'` in each of the two routes and a flipped
 * §1.4 in `server/tests/api/order-list-file-count.test.js`. Nothing in this
 * component needs to change.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ♿ IT IS A SOLE CARRIER, SO IT IS HELD TO THE SOLE-CARRIER BAR FROM THE START
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * There is no text beside it reading "3 files" — the glyph IS the message. So
 * `role="img"` plus an `aria-label` naming **the state AND the number**, exactly
 * the contract `components/LeadTimeRisk.js`'s `LeadTimeDot` and
 * `components/DueIndicator.js` follow. "Has attachments" would tell a screen
 * reader user strictly less than a sighted user gets, and this is their only
 * channel.
 *
 * 🔀 **CARRIES A NATIVE `title` SINCE 2026-08-16 — A DELIBERATE CONTRACT
 * CHANGE, REVERSING THE RULE BELOW THAT HELD UNTIL THEN.** Until this date the
 * marker carried NO native `title`, on purpose: a native `title` on a CHILD
 * wins over its parent's, and on the table layout this marker renders inside
 * a `<td>` that already carries `title={order.requested_by}` — so a `title`
 * here would SHADOW the name tooltip on exactly the pixels a user aims at,
 * the same measured defect recorded against `LeadTimeDot` and the unified
 * order-table Date-cell tooltip (*"two tooltips in one cell is the defect the
 * unification removed"*).
 *
 * 🔑 **The product owner's request is exactly the shadowing this used to
 * forbid, asked for on purpose**: hovering the CLIP itself should show which
 * files are attached; hovering the surrounding name/PO text should still show
 * the cell's own tooltip. Native `title` resolves innermost-wins, which is
 * precisely the two-zone behaviour wanted — so the fix is not a workaround,
 * it is the mechanism this docblock used to warn away from, now wanted. The
 * `title` lives ONLY on this marker's own wrapper `<span>`, never on an
 * ancestor, so the cell's `title={order.requested_by}` is UNCHANGED and still
 * answers every pixel the marker does not cover.
 *
 * ⚠️ **§3 of `OrderAttachmentMarker.test.js` used to pin the OPPOSITE — "the
 * cell keeps its tooltip AND nothing inside it carries one" — and is INVERTED
 * to match, not repaired**: the cell keeps its title unchanged; the marker
 * now carries one too, so `cell.querySelectorAll('[title]').length` goes
 * `0 → 1`. Built by `buildAttachmentTooltip` (`utils/orderFileTooltip.js`)
 * from the `attachment_files` list the server attaches beside `file_count`.
 *
 * 📏 **THE SHADE IS MEASURED, AND THE FIRST ONE FAILED.** It shipped for about an
 * hour as `text-gray-400 dark:text-gray-500`, which LOOKS like the muted
 * treatment every other secondary glyph on the row uses — and measured
 * **2.54:1 in light against the white row, i.e. FAILING** WCAG 1.4.11's 3:1
 * non-text minimum. That minimum applies here for the reason in the block above:
 * the clip is the sole carrier, so it is held to the same bar the amber shoot-date
 * dot was (`components/DueIndicator.js`, which shipped `bg-amber-500` at 2.15:1
 * for exactly the same reason — it *looked* like the right muted shade).
 * ✅ **`text-gray-500`, BARE, measured in Chromium against the real row: 4.83:1
 * light / 5.76:1 dark.**
 * 🪤 **NO `dark:` PAIR, AND ADDING ONE WOULD BE INERT** — `index.css`'s global
 * `html.dark … !important` table already maps this token, and measurement shows
 * `text-gray-500` and `text-gray-600` resolve to the SAME `rgb(156,163,175)` in
 * dark. A local pair would compile, ship, and change nothing, which is worse than
 * none because it reads as explicit dark support and stops the next person
 * checking. **Do not "restore the dark variant".**
 * ⚠️ **Do not re-mute it to match the neighbouring paperclips inside
 * `OrderAttachments`** — those sit BESIDE a filename, so they are redundant and
 * a caret-level shade is defensible there. This one has no text beside it.
 *
 * 🔴 **NOT a button, and not a link.** It reports; it does not act. The row's own
 * expand gesture is the way to the files, and it already spans the whole row —
 * so there is no 44px tap-target obligation here, because there is no tap
 * target. Making it clickable is a product change (it would need its own hit
 * area, its own focus ring and a decision about what "click the clip" opens),
 * not a refinement of this.
 */

/**
 * Renders nothing unless `count` is a real, positive number.
 *
 * @param {number} count
 * @param {Array<{label:?string, name:string}>} [files] — the filenames the
 *   native `title` is built from (2026-08-16). Optional and tolerated absent
 *   (a client ahead of its server, or a caller — none today — that never
 *   fetches it): the marker still renders on `count` alone, just with a
 *   count-only tooltip. See `buildAttachmentTooltip`.
 */
function OrderAttachmentMarker({ count, files, className = '' }) {
  // 🪤 `Number.isFinite` + `> 0`, never a truthiness test and never `!= null`.
  // During a deploy the client is briefly newer than the server, so `count` is
  // `undefined` on every row — and a truthiness test on a stringified count
  // would mark every one of them. "Every order has attachments" is a confident
  // lie, which is worse than the marker not appearing yet.
  if (!Number.isFinite(count) || count <= 0) return null;

  const label = `${count} file${count === 1 ? '' : 's'} attached`;

  return (
    <span
      // 🔑 `flex-shrink-0` is load-bearing on BOTH layouts: it renders next to a
      // `truncate`d name inside a fixed-width column that the user can drag down
      // to 120px. Without it the flex algorithm is free to squash the clip
      // instead of the name, and the marker silently disappears at exactly the
      // width where a buyer has made the column narrow.
      className={`inline-flex items-center flex-shrink-0 text-gray-500 ${className}`}
      role="img"
      aria-label={label}
      title={buildAttachmentTooltip(count, files)}
      data-testid="order-attachment-marker"
      data-file-count={count}
    >
      <PaperclipIcon className="h-3.5 w-3.5" />
    </span>
  );
}

export default OrderAttachmentMarker;
